// Toast podle prototypu (`showToast`, `setUndo`): jedna hláška dole uprostřed, fronta dalších,
// automatické zavření (2,6 s; s tlačítkem Zpět 6 s podle docs/04) a volitelné Zpět (Undo).
// Použití: `const toast = useToast(); toast.ukazat('Smazáno: zakázka', { zpet: () => obnovit() })`.
// Odložená akce: `{ zpet, poZavreni: (vraceno) => { if (!vraceno) odeslat() } }` — až už Zpět nejde.
// Odkaz: `{ odkaz: { text: 'Otevřít kartu', naKlik: () => navigate(…) } }` — po založení v okně, které se zavřelo.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import './komponenty.css';

export interface MoznostiToastu {
  /** Akce Zpět; s ní toast drží déle (6 s) a nabídne tlačítko „Zpět“. */
  zpet?: () => void;
  /** Vlastní doba zobrazení v ms. */
  trvaniMs?: number;
  /**
   * Zavolá se jednou, když hláška zmizí: `true` = uživatel dal Zpět, `false` = vypršela nebo ji
   * zavřelo `zavrit()`. Pro odložené akce (mazání přílohy se odešle, až už Zpět nejde).
   */
  poZavreni?: (vraceno: boolean) => void;
  /**
   * Ukázat hned: zobrazenou hlášku bez Zpět zavře a předběhne čekající hlášky bez Zpět. Pro Zpět po
   * mazání, které se odešle až po vypršení hlášky (jinak by Zpět přišlo na řadu pozdě).
   */
  prednost?: boolean;
  /** Text hlášky nese tajnou hodnotu (adresa s tokenem): na snímku obrazovky k hlášení se zakryje (`data-citlive`). */
  citlive?: boolean;
  /** Odkaz v hlášce (např. „Otevřít kartu“ po založení); s ním toast drží déle jako se Zpět. */
  odkaz?: { text: string; naKlik: () => void };
}

export interface ToastKontext {
  ukazat: (text: string, moznosti?: MoznostiToastu) => void;
  /** Zavře aktuální hlášku (bez provedení Zpět). */
  zavrit: () => void;
}

interface Zaznam extends MoznostiToastu {
  id: number;
  text: string;
  trvaniMs: number;
}

export const TOAST_TRVANI_MS = 2600;
export const TOAST_TRVANI_ZPET_MS = 6000;

const Kontext = createContext<ToastKontext | null>(null);

export function PoskytovatelToastu({ children }: { children: ReactNode }) {
  const [fronta, setFronta] = useState<Zaznam[]>([]);
  const dalsiId = useRef(1);
  const aktualni = fronta[0] ?? null;
  // Aktuální hláška pro `zavrit()` volané mimo render a id už vyřízených hlášek (poZavreni jen jednou).
  const aktualniRef = useRef<Zaznam | null>(null);
  const vyrizene = useRef(new Set<number>());

  useEffect(() => {
    aktualniRef.current = aktualni;
  }, [aktualni]);

  const odebrat = useCallback((zaznam: Zaznam, vraceno: boolean) => {
    if (vyrizene.current.has(zaznam.id)) return;
    vyrizene.current.add(zaznam.id);
    setFronta((f) => f.filter((z) => z.id !== zaznam.id));
    zaznam.poZavreni?.(vraceno);
  }, []);

  const ukazat = useCallback(
    (text: string, moznosti: MoznostiToastu = {}) => {
      const zaznam: Zaznam = {
        id: dalsiId.current++,
        text,
        zpet: moznosti.zpet,
        poZavreni: moznosti.poZavreni,
        odkaz: moznosti.odkaz,
        citlive: moznosti.citlive,
        trvaniMs:
          moznosti.trvaniMs ??
          (moznosti.zpet || moznosti.odkaz ? TOAST_TRVANI_ZPET_MS : TOAST_TRVANI_MS),
      };
      if (!moznosti.prednost) {
        setFronta((f) => [...f, zaznam]);
        return;
      }
      const zobrazena = aktualniRef.current;
      if (zobrazena && !zobrazena.zpet) odebrat(zobrazena, false);
      setFronta((f) => {
        const prvniBezna = f.findIndex((z) => !z.zpet);
        return prvniBezna === -1
          ? [...f, zaznam]
          : [...f.slice(0, prvniBezna), zaznam, ...f.slice(prvniBezna)];
      });
    },
    [odebrat],
  );

  const zavrit = useCallback(() => {
    const zaznam = aktualniRef.current;
    if (zaznam) odebrat(zaznam, false);
  }, [odebrat]);

  // Automatické zavření aktuální hlášky; další z fronty naskočí po ní.
  useEffect(() => {
    if (!aktualni) return undefined;
    const casovac = window.setTimeout(() => odebrat(aktualni, false), aktualni.trvaniMs);
    return () => window.clearTimeout(casovac);
  }, [aktualni, odebrat]);

  const hodnota = useMemo<ToastKontext>(() => ({ ukazat, zavrit }), [ukazat, zavrit]);

  const naOdkaz = () => {
    if (!aktualni) return;
    aktualni.odkaz?.naKlik();
    odebrat(aktualni, false);
  };

  const naZpet = () => {
    if (!aktualni) return;
    const { zpet } = aktualni;
    zpet?.();
    odebrat(aktualni, true);
  };

  return (
    <Kontext.Provider value={hodnota}>
      {children}
      {/* Živá oblast je v DOM stále, aby čtečka zachytila i první hlášku (bez role, ať nekoliduje
          s role="status" stránek — hlášku oznamuje aria-live). */}
      <div aria-live="polite" aria-atomic="true">
        {aktualni && (
          <div className="toast" data-testid="toast">
            <span data-citlive={aktualni.citlive ? '' : undefined}>{aktualni.text}</span>
            {aktualni.odkaz && (
              <button type="button" className="toast__zpet" onClick={naOdkaz}>
                {aktualni.odkaz.text}
              </button>
            )}
            {aktualni.zpet && (
              <button type="button" className="toast__zpet" onClick={naZpet}>
                Zpět
              </button>
            )}
          </div>
        )}
      </div>
    </Kontext.Provider>
  );
}

export function useToast(): ToastKontext {
  const kontext = useContext(Kontext);
  if (!kontext) {
    throw new Error('useToast je nutné volat uvnitř PoskytovatelToastu.');
  }
  return kontext;
}
